import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Flex, Grid, GridItem, HStack, Menu, MenuButton, MenuItem, MenuList, Text, Tooltip } from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import DashboardCard from "../../ISSB/Dashboard/components/DashboardCard";
import YoYChip from "../../Reports/Insights/components/YoYChip";
import { REPORTING_YEARS } from "../suppliersMock";
import { useGetSupplierOverviewData } from "../../../hooks/query/emissions-insight/emissions-insights-query";
import { useToast } from "@chakra-ui/react";
import { showErrorToast } from "../../../utils";
import LocalSpinner from "../../../components/LocalSpinner";

const fmtNum = (n) => Number(n || 0).toLocaleString("en-GB");
const fmtPct = (n) => `${Number(n || 0).toFixed(0)}%`;

const BigNumber = ({ children, color = "gray.800", pctChange = null }) => (
  <Box display="inline-block" whiteSpace="nowrap" sx={{ "--big": "clamp(20px, 2.6vw, 44px)" }}>
    <Flex align="center" gap={2}>
      <Text fontSize="var(--big)" fontWeight="bold" color={color} lineHeight="1" letterSpacing="-0.02em">
        {children}
      </Text>
      {pctChange != null && <YoYChip pctChange={pctChange} />}
    </Flex>
  </Box>
);

const CardTitle = ({ children, unit }) => (
  <Text fontSize="body.2xl" fontWeight="bold" color="teal">
    {children}{" "}
    {unit && <Text as="span" fontSize="body.s" fontWeight="bold" color="gray.700">{unit}</Text>}
  </Text>
);

const PROGRESS_BARS = [
  { key: "finalised", label: "Finalised", color: "#22C55E" },
  { key: "inProgress", label: "In progress", color: "#F59E0B" },
  { key: "notStarted", label: "Not started", color: "#FC8181" },
];

function StatusTab() {
  const toast = useToast();
  const [year, setYear] = useState(REPORTING_YEARS[REPORTING_YEARS.length - 1]);
  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);

  const { isFetching } = useGetSupplierOverviewData(
    [year, year - 1],
    null,
    null,
    null,
    {
      onSuccess: (data) => {
        setCurrentYearData(data?.[year] ?? null);
        setPreviousYearData(data?.[year - 1] ?? null);
      },
      onError: (err) => {
        if (err?.response?.status !== 404) {
          showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
        }
      },
      cacheTime: 60,
    }
  );

  const stats = useMemo(() => {
    if (!currentYearData) return null;
    const engaged = currentYearData.supplierCountWithRequestedData || 0;
    const finalised = currentYearData.totalSuppliersWithAllFinalizedData || 0;
    const verified = currentYearData.verifiedSupplierCount || 0;
    const inProgress = Math.max(0, engaged - finalised);
    const notStarted = 0; // not available from overview endpoint
    const total = engaged || 1;

    const prevEngaged = previousYearData?.supplierCountWithRequestedData || 0;
    const prevFinalised = previousYearData?.totalSuppliersWithAllFinalizedData || 0;

    return {
      engaged,
      finalised,
      verified,
      inProgress,
      notStarted,
      total,
      prevEngaged,
      prevFinalised,
      byStatus: { finalised, inProgress, notStarted },
    };
  }, [currentYearData, previousYearData]);

  if (isFetching && !stats) return <Box mt={6}><LocalSpinner /></Box>;

  const totalTasks = stats?.total || 1;

  return (
    <Box mt={6}>
      <Flex justify="flex-end" mb={4}>
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            borderRadius="full"
            border="1px solid"
            borderColor="gray.300"
            bg="transparent"
            color="gray.600"
            fontWeight="normal"
            rightIcon={<CaretDown size={14} />}
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
          >
            {year}
          </MenuButton>
          <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="120px">
            {[...REPORTING_YEARS].reverse().map((y) => (
              <MenuItem key={y} onClick={() => setYear(y)} fontWeight={year === y ? "bold" : "normal"}>
                {y}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} mb={4}>
        {/* Suppliers engaged */}
        <GridItem>
          <DashboardCard bg="#F7FAFC">
            <CardTitle>Suppliers engaged</CardTitle>
            <BigNumber>{fmtNum(stats?.engaged)}</BigNumber>
            <Text fontSize="body.s" color="gray.500" mt={2}>
              {stats?.prevEngaged != null ? `${fmtNum(stats.prevEngaged)} in ${year - 1}` : "requesting data"}
            </Text>
          </DashboardCard>
        </GridItem>

        {/* Finalised */}
        <GridItem>
          <DashboardCard bg="#F7FAFC">
            <CardTitle>Finalised</CardTitle>
            <BigNumber>{fmtNum(stats?.finalised)}</BigNumber>
            <Text fontSize="body.s" color="gray.500" mt={2}>of {fmtNum(stats?.engaged)} engaged</Text>
          </DashboardCard>
        </GridItem>

        {/* Verified */}
        <GridItem>
          <DashboardCard bg="#F7FAFC">
            <CardTitle>Verified</CardTitle>
            <BigNumber color="status.green">{fmtNum(stats?.verified)}</BigNumber>
            <Text fontSize="body.s" color="gray.500" mt={2}>data verified suppliers</Text>
          </DashboardCard>
        </GridItem>

        {/* Task progress breakdown */}
        <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
          <DashboardCard bg="#F7FAFC">
            <CardTitle>Submission progress</CardTitle>
            <Text fontSize="body.s" color="gray.500" mb={4}>
              Finalised vs in-progress for {year} across all engaged suppliers.
            </Text>
            <Flex h="14px" borderRadius="full" overflow="hidden" bg="gray.100" mb={4}>
              {PROGRESS_BARS.map((s) => {
                const count = stats?.byStatus?.[s.key] || 0;
                const pct = (count / totalTasks) * 100;
                if (pct <= 0) return null;
                return (
                  <Tooltip key={s.key} label={`${s.label}: ${fmtNum(count)} (${fmtPct(pct)})`} hasArrow placement="top">
                    <Box w={`${pct}%`} bg={s.color} />
                  </Tooltip>
                );
              })}
            </Flex>
            <HStack spacing={8} flexWrap="wrap">
              {PROGRESS_BARS.map((s) => {
                const count = stats?.byStatus?.[s.key] || 0;
                const pct = (count / totalTasks) * 100;
                return (
                  <HStack key={s.key} spacing={3} align="flex-start">
                    <Box w="10px" h="10px" borderRadius="full" bg={s.color} mt={1.5} flexShrink={0} />
                    <Box textAlign="left">
                      <Text fontSize="heading.s" fontWeight="bold" color="gray.800">
                        {fmtNum(count)}{" "}
                        <Text as="span" fontSize="body.s" color="gray.400" fontWeight="500">({fmtPct(pct)})</Text>
                      </Text>
                      <Text fontSize="body.s" color="gray.500">{s.label}</Text>
                    </Box>
                  </HStack>
                );
              })}
            </HStack>
          </DashboardCard>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default StatusTab;
